import { inject, singleton } from "tsyringe";
import {
  Browser,
  ContextRepository,
  FandomContextGroups,
  Platform,
  Platforms,
} from "@identity/utils";
import { WINDOW } from "@identity/symbols";

@singleton()
export class AdTargetingService {
  constructor(
    @inject(WINDOW) private readonly window: Window,
    @inject(ContextRepository) private contextRepository: ContextRepository,
    @inject(Browser) private browser: Browser,
    @inject(Platform) private readonly platform: Platform,
  ) {}

  init() {
    const { ready, coppa, optOut } = this.window.fandomCmp;

    if (ready && !coppa && !optOut) {
      this.setAdTargetingGroup();
      this.setBrowser();
    }

    this.setBrand();
  }

  public setBrowser() {
    this.contextRepository.set(
      FandomContextGroups.targeting,
      "browser",
      this.browser.getBrowser().split(" ")[0],
    );
  }

  public setBrand() {
    let brand: string = this.platform.get();

    if (
      brand === Platforms.FANDOMDESKTOP ||
      brand === Platforms.FANDOMMOBILE ||
      brand === Platforms.F2
    ) {
      brand = "fandom";
    }

    if (brand === Platforms.METACRITIC_NEUTRON) {
      brand = Platforms.METACRITIC;
    }

    if (brand === Platforms.TVGUIDE_MTC) {
      brand = Platforms.TVGUIDE;
    }

    this.contextRepository.set(FandomContextGroups.targeting, "brand", brand);
  }

  public getRandomGroup(): number {
    return Math.ceil((Math.random() % 20) * 20) * 5;
  }

  /*
    Used to target ads to % of users. We generate a random number between 0 and 100, and then round it to the nearest 5.
    Then, we create an array of numbers from 100 to that number. Then in GAM we use adGroup key-val to target ads to
    certain % of users. For example:
    Ad visible for 50% of users will be visible for everyone with adGroup=100,95,90,85,80,75,70,65,60,55,50
    Ad visible for 80% of users will be visible for everyone with adGroup=100,95,90,85,80
  */
  public setAdTargetingGroup() {
    const generatedGroup = this.getRandomGroup();
    const groups = Array.from(
      { length: 21 },
      (_, index) => 100 - index * 5,
    ).filter((group) => group >= generatedGroup && group > 0);
    this.contextRepository.set(
      FandomContextGroups.targeting,
      "adGroup",
      groups.toString(),
    );
  }
}
